import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { flushSync } from 'react-dom';

/*
 * Light/dark theme for the marketing landing.
 *
 * The page follows the system colour scheme. The header toggle sets a manual override
 * that survives reloads; the override is dropped when the system scheme changes, so the
 * page never stays stuck on the opposite of what the OS just switched to.
 *
 * The resolved theme lives on <html data-theme> next to the `chefty-landing` class. The
 * inline script in index.html sets both before first paint; this hook keeps them in sync
 * afterwards. landing.css defines every colour on `html.chefty-landing`, so <body>, the
 * solid sticky header and the bottom safe-area strip all resolve one --bg. That matters
 * on iOS 26, which ignores <meta name="theme-color"> and tints the status bar and
 * toolbars from those surfaces. The meta tag is still updated for older Safari and for
 * other mobile browsers.
 *
 * The switch is instant on purpose. With a fade or a View Transition, Safari repaints
 * its status bar only after the animation ends, so the chrome visibly lags the page.
 * Only the toggle's own icon animates.
 */

export type Theme = 'light' | 'dark';

/** Keep in sync with the inline script in apps/client/index.html. */
const STORAGE_KEY = 'chefty-theme';
const LANDING_CLASS = 'chefty-landing';
/** Suppresses hover/press transitions for the frame the theme flips in (landing.css). */
const SWITCHING_CLASS = 'theme-switching';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const systemTheme = (): Theme => (window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light');

function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function storeTheme(theme: Theme | null): void {
  try {
    if (theme) localStorage.setItem(STORAGE_KEY, theme);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage blocked: the choice lasts for this visit only */
  }
}

const themeMetas = () =>
  Array.from(document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'));

/** Writes the theme to <html>, then re-tints the browser chrome from the resolved page colour. */
function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  const bg = getComputedStyle(root).getPropertyValue('--bg').trim();
  if (bg) themeMetas().forEach((meta) => (meta.content = bg));
}

export function useLandingTheme() {
  const [theme, setTheme] = useState<Theme>(() => storedTheme() ?? systemTheme());

  // Hand <html> and <body> to landing.css while the landing is mounted; give the
  // theme-color tags back their defaults when the visitor moves to another page.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const metas = themeMetas();
    const defaults = metas.map((meta) => meta.content);
    root.classList.add(LANDING_CLASS);
    return () => {
      root.classList.remove(LANDING_CLASS, SWITCHING_CLASS);
      delete root.dataset.theme;
      metas.forEach((meta, i) => (meta.content = defaults[i]));
    };
  }, []);

  useLayoutEffect(() => applyTheme(theme), [theme]);

  /**
   * Flips every surface in one frame. Transitions stay off for two frames, so a button
   * with a background transition doesn't fade in after the rest of the page has flipped.
   */
  const switchTo = useCallback((next: Theme) => {
    const root = document.documentElement;
    root.classList.add(SWITCHING_CLASS);
    flushSync(() => setTheme(next));
    requestAnimationFrame(() =>
      requestAnimationFrame(() => root.classList.remove(SWITCHING_CLASS)),
    );
  }, []);

  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY);
    const onSystemChange = () => {
      storeTheme(null);
      switchTo(systemTheme());
    };
    query.addEventListener('change', onSystemChange);
    return () => query.removeEventListener('change', onSystemChange);
  }, [switchTo]);

  const toggle = useCallback(() => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    // Choosing what the system already shows is the same as having no override.
    storeTheme(next === systemTheme() ? null : next);
    switchTo(next);
  }, [theme, switchTo]);

  return { theme, toggle };
}
